/**
 * The game loop — where the socket, the input, the prediction and the renderer
 * meet.
 *
 * Two clocks, deliberately separate:
 *
 * - The **render loop** runs at the display's refresh rate. It interpolates,
 *   eases the camera and draws. It never decides anything.
 * - The **sim clock** advances at the server's rate, and is what movement is
 *   submitted against. Deriving it from snapshots rather than from a local
 *   timer keeps the client's idea of "which tick is it" anchored to the
 *   server's, which is what prediction needs to be checkable.
 */

import {
  DIRECTION_VECTORS,
  MS_PER_TICK,
  type Direction,
  type IntentType,
  type LogEvent,
  type LogMessage,
  type RejectedMessage,
  type EntitySnapshot,
  type SelfSnapshot,
  type SkySnapshot,
  type SnapshotMessage,
  type WelcomeMessage,
} from '@annwn/protocol';

import { Input } from './input/input.js';
import { AimMenu } from './ui/aim.js';
import { aimReadoutText, lineOfFire } from './ui/ranged-aim.js';
import { RangedAimOverlay } from './ui/ranged-overlay.js';
import {
  actionMenuItems,
  actionMenuTitle,
  resolveVerbChoice,
  type VerbContext,
} from './ui/action-verbs.js';
import type { ActionMenuRequest } from './ui/menu-overlay.js';
import type { MenuItem } from '@annwn/ui';
import { examineCard, type ExamineCard, type ExamineSubject } from './ui/examine-view-model.js';
import type { HudState } from './ui/hud-view-model.js';
import { ConsentPrompt } from './ui/consent.js';
import { GestureMenu } from './ui/gestures.js';
import { isTalkable, TalkMenu, type TalkMenuRequest } from './ui/talk.js';
import { InteractMenu, isHelplessTarget } from './ui/interact.js';
import { loadKeymap, type Action } from './input/keymap.js';
import { Connection } from './net/connection.js';
import { createRenderer, loadAtlas } from './render/atlas.js';
import type { Renderer, Sprite } from './render/renderer.js';
import {
  buildScene,
  cursorSprite,
  gridSize,
  selfSprite,
  tileAtPixel,
  type Viewport,
} from './render/scene.js';
import { ClientWorld } from './world/client-world.js';
import { Camera } from './world/interpolation.js';
import { Look } from './world/look.js';
import { Prediction } from './world/prediction.js';

/**
 * The two context-menu rows that are about the *square* rather than about the
 * rules. Prefixed so neither can ever collide with an `IntentType` coming back
 * from the same menu.
 */
const MENU_LOOK = 'menu:look';
const MENU_TARGET = 'menu:target';

export interface GameOptions {
  readonly canvas: HTMLCanvasElement;
  readonly serverUrl: string;
  readonly name: string;

  /**
   * The background chosen at creation (M7-03), for a brand-new character's first
   * join. Absent for a returning player — the server has their body already.
   */
  readonly backgroundId?: string | null;
  onLog?(events: readonly LogEvent[]): void;
  onStatus?(status: string): void;

  /** Open or close the chat line. The DOM field lives in the shell (M0-06). */
  onChat?(open: boolean): void;

  /** A message that never went near the server — a hint, a refusal. */
  onNote?(text: string): void;

  /** The viewer's own condition, every snapshot — the diagram and the strip. */
  onSelf?(self: SelfSnapshot | null, tick: number): void;

  /** Who the viewer is, once the server has said. */
  onIdentity?(actorId: string): void;

  /** Open or close the unabridged battle view. */
  onBattle?(open: boolean): void;

  /** Open or close the satchel (M3-03). Its contents ride on `onSelf`. */
  onSatchel?(open: boolean): void;

  /** Open or close the character sheet (RC2-03). Its contents ride on `onSelf`. */
  onSheet?(open: boolean): void;

  /**
   * Open the action menu with this request, or close it with `null` (RC1-05).
   * The focused `Popup`+`Menu` island lives in the shell; `Game` builds the
   * items and resolves the chosen verb through `chooseAction`.
   */
  onActions?(request: ActionMenuRequest | null): void;

  /** Open the button-driven conversation popup, or close it with `null`. */
  onTalk?(request: TalkMenuRequest | null): void;

  /** What the sky is doing, every snapshot (M3-05). */
  onSky?(sky: SkySnapshot): void;

  /**
   * Show the look card, or close it with `null` (RC2-07).
   *
   * The subject comes off the snapshot the client already has and the prose off
   * the server's `examined` answer, so this fires when the two have met — one
   * card per question asked, replacing the one before it.
   */
  onExamine?(card: ExamineCard | null): void;

  /**
   * What the hotbar needs and the snapshot cannot say: which overlays are open,
   * whether the look cursor is out, who is within reach (RC2-07).
   */
  onHud?(state: HudState): void;
}

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly options: GameOptions;

  private renderer: Renderer | null = null;
  private world: ClientWorld | null = null;
  private readonly prediction = new Prediction();
  private readonly camera = new Camera();
  private readonly input: Input;
  private readonly connection: Connection;
  private readonly look = new Look();
  private readonly aim = new AimMenu();
  private rangedAimActive = false;
  private rangedTarget: EntitySnapshot | null = null;
  private rangedOverlay: RangedAimOverlay | null = null;
  private readonly interact = new InteractMenu();

  /**
   * The one prompt the world opens (M4-05). Fed from the log stream, and given
   * the keyboard ahead of every menu below: a question about your own leg
   * outranks whatever you were doing.
   */
  private readonly consent = new ConsentPrompt();

  /** The gestures (M4-07) — speech for anybody the cauldron has silenced. */
  private readonly gestures = new GestureMenu();

  /** Asking the Meddyg for something (M4-08). */
  private readonly talk = new TalkMenu();

  /**
   * The action menu (RC1-05) is now a focused `Popup`+`Menu` island in the shell
   * rather than a keystroke prompt, so `Game` holds only the snapshot it was
   * opened against — to resolve the chosen verb — and whether it is open, to
   * know the keyboard has been handed away.
   */
  private verbContext: VerbContext | null = null;
  private actionMenuOpen = false;

  /**
   * The tile the open menu is about, or `null` when it is the "here" menu the
   * camp key opens. A contextual action card may name a square; both routes use
   * one component and one resolution rule (RC2-07).
   */
  private menuTile: { readonly x: number; readonly y: number } | null = null;

  /**
   * What the look cursor was over when the last `examine` went out (RC2-07).
   *
   * Held rather than re-derived when the answer lands, because the player may
   * have walked a step in the meantime and the card must describe the tile that
   * was asked about, not the one the cursor has since been re-anchored to.
   */
  private examineSubject: ExamineSubject | null = null;

  /** The viewer's own snapshot: the body the aim menu offers and the diagram draws. */
  private ownSelf: SelfSnapshot | null = null;

  /** Actors the log pane wants lit up on the grid. */
  private highlighted: ReadonlySet<string> = new Set();

  private battleOpen = false;
  private satchelOpen = false;
  private sheetOpen = false;

  private viewport: Viewport = { width: 0, height: 0, cellWidth: 16, cellHeight: 16 };

  /** The server tick the client believes it is on. */
  private tick = 0;
  private tickAccumulator = 0;
  private lastFrameAt = 0;
  private frame = 0;
  private running = false;

  constructor(options: GameOptions) {
    this.options = options;
    this.canvas = options.canvas;

    this.input = new Input(loadKeymap(globalThis.localStorage));

    this.connection = new Connection({
      onWelcome: (message) => this.onWelcome(message),
      onSnapshot: (message) => this.onSnapshot(message),
      onRejected: (message) => this.onRejected(message),
      onLog: (message: LogMessage) => this.onLog(message),
      onError: (error) => this.options.onStatus?.(error.message),
      onClose: () => this.options.onStatus?.('Disconnected. Reload to rejoin.'),
    });
  }

  async start(): Promise<void> {
    const atlas = await loadAtlas();

    this.viewport = {
      ...this.viewport,
      cellWidth: atlas.cellWidth,
      cellHeight: atlas.cellHeight,
    };

    this.renderer = createRenderer(this.canvas, atlas);
    this.rangedOverlay = new RangedAimOverlay(this.canvas);
    this.resize();

    globalThis.addEventListener('resize', this.resize);
    // Diagnostic capture at the application boundary. If a HUD island sits over
    // the canvas, the canvas listener below cannot tell us that it missed a
    // click; window capture can, including the element that actually received
    // it. Kept to Look only so normal play does not flood the console.
    globalThis.addEventListener('pointerdown', this.onLookPointerTrace, { capture: true });
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    // Own the browser gesture on the element that is actually being inspected.
    // A window-level capture listener proved brittle when overlays were added:
    // the canvas is the complete and honest boundary for a tile question.
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
    this.input.attach(globalThis.window);
    globalThis.addEventListener('keydown', this.onAimKey, { capture: true });

    this.options.onStatus?.(`Connecting… (${this.renderer.kind})`);
    await this.connection.connect({
      url: this.options.serverUrl,
      name: this.options.name,
      backgroundId: this.options.backgroundId ?? null,
    });

    this.running = true;
    this.lastFrameAt = performance.now();
    this.frame = requestAnimationFrame(this.loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    globalThis.removeEventListener('resize', this.resize);
    globalThis.removeEventListener('pointerdown', this.onLookPointerTrace, { capture: true });
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.input.detach();
    globalThis.removeEventListener('keydown', this.onAimKey, { capture: true });
    this.renderer?.dispose();
    this.rangedOverlay?.dispose();
    this.rangedOverlay = null;
    void this.connection.disconnect();
  }

  /**
   * Speak. Spatial, so there is no channel to choose — only a range, which the
   * server owns (design spec §8).
   */
  say(text: string): void {
    if (text.trim().length === 0) return;
    this.connection.send('say', { text: text.trim() }, this.connection.nextSequence());
  }

  /** The keyboard has gone to the chat line, or come back from it. */
  setChatOpen(open: boolean): void {
    this.input.setEnabled(!open);
    // Look mode and the chat line are both modal; being in two modes at once
    // is a state neither of them was written for.
    if (open) this.closeLook();
  }

  private readonly resize = (): void => {
    // Render at device resolution so 16-pixel cells stay 16 real pixels on a
    // HiDPI display — a glyph grid scaled by the browser is a blurred one.
    const ratio = Math.min(globalThis.devicePixelRatio || 1, 2);
    const width = Math.floor(this.canvas.clientWidth * ratio);
    const height = Math.floor(this.canvas.clientHeight * ratio);

    if (width === 0 || height === 0) return;

    this.canvas.width = width;
    this.canvas.height = height;
    this.viewport = { ...this.viewport, width, height };

    this.renderer?.resize(width, height);
  };

  private onWelcome(message: WelcomeMessage): void {
    this.world = new ClientWorld(message.map.width, message.map.height);
    this.tick = message.tick;
    this.options.onIdentity?.(message.actorId);
    // Clear the connecting line rather than replace it with a second one: the
    // strip is the player's body and the weather, and "connected as actor-3" is
    // neither. The join goes to the chronicle, where the record belongs.
    this.options.onStatus?.('');
    this.options.onNote?.(`You wake in the valley. (${this.renderer?.kind})`);
  }

  private onSnapshot(message: SnapshotMessage): void {
    const world = this.world;
    if (!world) return;

    const snapshot = message.snapshot;
    world.apply(snapshot, performance.now());

    // Re-anchor the clock. Drifting ahead of the server would mean predicting
    // against cooldowns that have not elapsed yet and being corrected for it.
    this.tick = snapshot.tick;
    this.tickAccumulator = 0;

    if (snapshot.self) {
      const first = this.prediction.self === null;
      this.prediction.reconcile(world, snapshot.self, snapshot.ack, this.tick);
      if (first) this.camera.snapTo(snapshot.self.x, snapshot.self.y);
    }

    // The diagram and the status strip read from here rather than polling, so
    // "reflects every wound state change within one snapshot" is true by
    // construction rather than by a timer that happens to be fast enough.
    this.ownSelf = snapshot.self;
    this.talk.updateSelf(snapshot.self);
    // Target selection borrows the Look cursor, but it is no longer a valid
    // mode once the bow has left the player's hands (death and resurrection
    // are the common path). Leaving this flag behind makes every later canvas
    // click try to target instead of inspect, even when the Look button is lit.
    const rangedWeapon = snapshot.self?.combat?.weapon;
    if (
      this.rangedAimActive &&
      (rangedWeapon?.range === null ||
        rangedWeapon?.range === undefined ||
        (rangedWeapon.arrows ?? 0) < 1 ||
        snapshot.self?.combat?.down)
    ) {
      this.closeRangedAim();
      if (this.look.active) this.closeLook();
    }
    if (this.rangedTarget) {
      const currentTarget = world.entity(this.rangedTarget.id);
      if (currentTarget) this.rangedTarget = currentTarget;
      else this.closeRangedAim();
    }
    this.options.onSelf?.(snapshot.self, snapshot.tick);
    if (this.talk.active) {
      const request = this.talk.request();
      if (request) this.options.onTalk?.(request);
    }
    this.options.onSky?.(snapshot.sky);
    // The bar reads availability off the same snapshot the strip does, so a
    // slot can never say a verb is open that the server has just closed.
    this.publishHud();
  }

  private onRejected(message: RejectedMessage): void {
    this.prediction.reject(message.seq);
    if (message.reason !== 'illegal') {
      console.warn(`[net] intent ${message.seq} refused: ${message.reason}`, message.detail);
      return;
    }

    // An `illegal` move is ordinarily just a wall and is not worth a line. A
    // fight is different: every direction is refused, and saying nothing makes
    // a deliberate break-off rule indistinguishable from a movement fault.
    if (message.detail?.includes('must break off before moving') === true) {
      this.options.onNote?.('You are engaged. Press A to strike, or D to break off.');
      return;
    }
    if (message.detail?.includes('is still breaking off') === true) {
      this.options.onNote?.('You are breaking off. Hold your ground a moment.');
      return;
    }

    // A refused *say* is not silent either: swallowing the rate limit leaves
    // the player wondering where their words went.
    if (message.detail?.includes('too fast') === true) {
      this.options.onNote?.('You are speaking too quickly.');
      return;
    }

    if (message.detail?.includes('cannot be seen from here') === true) {
      this.options.onNote?.('You cannot make that out from here.');
    }
  }

  private readonly loop = (now: number): void => {
    if (!this.running) return;

    const delta = Math.min(now - this.lastFrameAt, 250);
    this.lastFrameAt = now;

    this.advanceClock(delta);
    this.handleCommands();
    this.submitInput();
    this.render(now, delta);

    this.frame = requestAnimationFrame(this.loop);
  };

  /**
   * Run the local tick counter forward between snapshots, so a player pressing
   * a key mid-tick is not held until the next packet arrives. Snapshots
   * overwrite it; this only fills the gaps.
   */
  private advanceClock(deltaMs: number): void {
    this.tickAccumulator += deltaMs;
    while (this.tickAccumulator >= MS_PER_TICK) {
      this.tickAccumulator -= MS_PER_TICK;
      this.tick += 1;
    }
  }

  /**
   * One-shot presses: opening the chat line, and driving the look cursor.
   *
   * Drained every frame whether or not anything is listening — a queue that is
   * only read in one mode is a queue that fires a burst of stale commands the
   * moment that mode opens.
   */
  private handleCommands(): void {
    for (const action of this.input.takeCommands()) this.command(action);
  }

  private command(action: Action): void {
    switch (action.kind) {
      case 'say':
        this.look.close();
        this.options.onChat?.(true);
        return;

      case 'cancel':
        // The action menu owns the keyboard while it is open (input is
        // suspended), so its Escape is the `Popup`'s, not this — but keep the
        // safety net for any non-keyboard cancel that reaches here.
        if (this.actionMenuOpen) {
          this.chooseAction(null);
          return;
        }
        if (this.satchelOpen) {
          this.setSatchelOpen(false);
          return;
        }
        if (this.sheetOpen) {
          this.setSheetOpen(false);
          return;
        }
        if (this.battleOpen) {
          this.setBattleOpen(false);
          return;
        }
        if (this.talk.active) {
          this.closeTalk();
          this.options.onNote?.('You think better of it.');
          return;
        }
        if (this.gestures.active) {
          this.gestures.close();
          this.options.onNote?.('You keep your hands to yourself.');
          return;
        }
        if (this.interact.active) {
          this.interact.close();
          this.options.onNote?.('You leave them be.');
          return;
        }
        if (this.aim.active) {
          this.aim.close();
          this.options.onNote?.('You lower your aim.');
          return;
        }
        if (this.rangedAimActive) {
          this.closeRangedAim();
          if (this.look.active) this.closeLook();
          this.options.onNote?.('You lower the bow.');
          return;
        }
        if (this.look.active) {
          this.closeLook();
          this.options.onNote?.('You look away.');
        }
        return;

      case 'aim':
        this.openAim();
        return;

      case 'target':
        this.targetAtCursor();
        return;

      case 'interact':
        this.openInteract();
        return;

      case 'forage':
        this.forage();
        return;

      case 'kindle':
        this.kindle();
        return;

      case 'camp':
        this.openVerbs();
        return;

      case 'gesture':
        this.options.onNote?.(this.gestures.show());
        return;

      case 'satchel':
        this.toggleSatchel();
        return;

      case 'sheet':
        this.toggleSheet();
        return;

      case 'guard':
        this.toggleGuard();
        return;

      case 'engage':
        this.engage();
        return;

      case 'disengage':
        this.connection.send('disengage', {}, this.connection.nextSequence());
        return;

      case 'loose':
        this.looseArrow();
        return;

      case 'sneak':
        this.toggleSneak();
        return;

      case 'battle':
        this.setBattleOpen(!this.battleOpen);
        return;

      case 'examine':
        this.toggleLook();
        return;

      case 'move':
        // Only meaningful in look mode; walking is held input, handled in
        // `submitInput` from `current()`.
        if (this.look.active) this.stepCursor(action.direction);
        return;
    }
  }

  /**
   * Open the aim menu against whatever is adjacent (M2-03 + M2-06).
   *
   * In an exchange, `a` is a plain, immediate strike at the opponent already
   * holding the player. Outside one, it opens the called-shot menu: two
   * keystrokes total, `a` then the zone.
   */
  private openAim(): void {
    if (
      this.ownSelf?.combat?.weapon?.range !== null &&
      this.ownSelf?.combat?.weapon?.range !== undefined
    ) {
      if (this.rangedAimActive && this.rangedTarget) {
        this.looseArrow();
        return;
      }
      this.options.onNote?.(
        'No target locked. Press T, move the × onto a creature, then press T again.',
      );
      return;
    }

    const targetId = this.ownSelf?.combat?.engagedWith;
    if (targetId) {
      this.connection.send('attack', { targetId }, this.connection.nextSequence());
      this.options.onNote?.('You strike.');
      return;
    }

    const parts = this.ownSelf?.combat?.parts ?? [];
    const prompt = this.aim.show(parts);
    if (prompt) this.options.onNote?.(prompt);
  }

  private closeRangedAim(): void {
    this.rangedAimActive = false;
    this.rangedTarget = null;
    this.rangedOverlay?.hide();
    this.publishHud();
  }

  /** Start target selection, or confirm the actor under the existing look cursor. */
  private targetAtCursor(): void {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return;

    const range = this.ownSelf?.combat?.weapon?.range;
    if (range === null || range === undefined) {
      this.options.onNote?.('You need a ranged weapon in hand to mark a distant target.');
      return;
    }
    if ((this.ownSelf?.combat?.weapon?.arrows ?? 0) < 1) {
      this.options.onNote?.('You have no arrows left.');
      return;
    }

    const at = this.look.at;
    if (at) {
      if (this.markTarget(at)) this.closeLook();
      return;
    }

    this.rangedAimActive = true;
    this.rangedTarget = null;
    this.look.open(self);
    this.options.onExamine?.(null);
    this.publishHud();
    this.options.onNote?.(
      'Choose a target: move the × with the direction keys, then press T again.',
    );
  }

  private rangedReadout() {
    const self = this.prediction.self;
    const world = this.world;
    const target = this.rangedTarget;
    if (!self || !world || !target) return null;
    return lineOfFire(world, self, target, world.sky.wind);
  }

  private looseArrow(): void {
    const weapon = this.ownSelf?.combat?.weapon;
    const range = weapon?.range;
    const readout = this.rangedReadout();
    if (
      !this.rangedAimActive ||
      !this.rangedTarget ||
      range === null ||
      range === undefined ||
      !readout
    ) {
      this.options.onNote?.('Raise the bow and lock a target first.');
      return;
    }
    if ((weapon?.arrows ?? 0) < 1) {
      this.options.onNote?.('You have no arrows left.');
      return;
    }
    if (readout.range > range) {
      this.options.onNote?.('That target is beyond the bow’s range.');
      return;
    }
    if (!readout.clear) {
      this.options.onNote?.('The line of fire is blocked.');
      return;
    }
    this.connection.send(
      'attack',
      { targetId: this.rangedTarget.id },
      this.connection.nextSequence(),
    );
    this.closeRangedAim();
  }

  /**
   * The aim menu's second keystroke, intercepted before the keymap sees it.
   *
   * Read straight off the raw event rather than routed through a binding: the
   * menu's keys are *labels on a prompt*, not rebindable actions, and putting
   * nine of them in the keymap would collide with movement on every layout.
   */
  private readonly onAimKey = (event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // A surgeon is asking for your leg (M4-05). First refusal, ahead of every
    // menu: nothing else you might have been pressing a key for matters as
    // much, and a question you can walk away from is not consent.
    if (this.consent.active) {
      event.preventDefault();
      event.stopPropagation();
      this.answerConsent(event.key);
      return;
    }

    // The gestures, on the same one-key-to-choose pattern as everything else.
    if (this.gestures.active) {
      event.preventDefault();
      event.stopPropagation();
      this.resolveGesture(event.key);
      return;
    }

    // The verb menu gets first refusal for the same reason the aim menu does:
    // while it is open, `s` and `l` and `d` are labels on a prompt rather than
    // bindings, and `x` must not fall through to examine.
    if (this.interact.active) {
      event.preventDefault();
      event.stopPropagation();
      this.resolveInteract(event.key);
      return;
    }

    // The camp menu no longer intercepts keys here (RC1-05): it is a focused
    // `Popup`+`Menu` island that owns the keyboard while it is open, with the
    // game's own input suspended — so `b` at the fire picks "lay a fire" in the
    // menu and never walks you out of your camp.

    if (!this.aim.active) return;

    // Captured ahead of the keymap: while the menu is open its letters are
    // labels on a prompt rather than bindings, so `g` picks a leg instead of
    // raising a guard, and exactly one keystroke resolves the menu either way.
    event.preventDefault();
    event.stopPropagation();
    this.resolveAim(event.key);
  };

  /**
   * Watch the log for a question addressed to us, and say so.
   *
   * The log still goes to the shell unchanged — the request is a line in it
   * like everything else, so a player who answers by reflex can still read
   * afterwards what they agreed to.
   */
  private onLog(message: LogMessage): void {
    const viewerId = this.ownSelf?.id ?? '';
    const prompt = this.consent.observe(message.events, viewerId, (id) => this.nameOf(id));
    const talker = this.talk.targetId;
    const reply =
      talker === null
        ? undefined
        : message.events
            .filter(
              (event) =>
                event.type === 'said' &&
                event.actors[0] === talker &&
                event.actors[1] === viewerId &&
                event.refs[0] === 'villager',
            )
            .at(-1);

    // The answer to a look goes to the card, not to the chronicle (RC2-07).
    // The chronicle is a *record* of what happened and a look is a *question*;
    // a player arrowing the cursor over six tiles used to bury the four lines
    // they had just read under six more, and the card holds the answer still
    // while they read it.
    const answered = message.events.filter((event) => event.type === 'examined');
    if (answered.length > 0) {
      console.info('[look-debug] client received examined event', {
        count: answered.length,
        events: answered.map((event) => ({ text: event.text })),
        subjectWaiting: this.examineSubject,
      });
    }
    const rest =
      answered.length > 0
        ? message.events.filter((event) => event.type !== 'examined')
        : message.events;

    this.options.onLog?.(rest);
    for (const event of answered) this.showExamined(event.text ?? '');
    if (reply?.text) {
      const request = this.talk.request(reply.text);
      if (request) this.options.onTalk?.(request);
    }
    if (prompt) this.options.onNote?.(prompt);
  }

  /**
   * Marry the server's description to the tile the client asked about.
   *
   * The subject was captured when the request went out, so an answer that
   * arrives after the player has taken a step still describes the square they
   * pointed at rather than the one the cursor has since been re-anchored to.
   */
  private showExamined(text: string): void {
    const subject = this.examineSubject;
    if (!subject) {
      console.warn('[look-debug] client dropped examined event: no subject is waiting', { text });
      return;
    }
    console.info('[look-debug] client opening examine card', { subject, text });
    this.options.onExamine?.(examineCard(subject, text));
  }

  /** Everything the client can already see on a square, for the look card. */
  private subjectAt(at: { x: number; y: number }): ExamineSubject {
    const world = this.world;
    const self = this.prediction.self;
    const own = self ? self.x === at.x && self.y === at.y : false;

    return {
      x: at.x,
      y: at.y,
      own,
      // `self` is not in the actor list — it travels on its own field — so the
      // viewer's own square is filled in by hand rather than left as bare
      // ground under a description that is plainly about a person.
      actor: world?.actorAt(at.x, at.y) ?? (own ? (this.ownSelf ?? undefined) : undefined),
      flora: world?.floraAt(at.x, at.y),
      carcass: world?.carcasses.find((body) => body.x === at.x && body.y === at.y),
      tile: world?.tileAt(at.x, at.y),
    };
  }

  /** Whatever the snapshot calls them, or their id if they are out of sight. */
  private nameOf(id: string): string {
    return this.world?.entity(id)?.name ?? id;
  }

  private answerConsent(key: string): void {
    const answer = this.consent.answer(key);
    if (!answer) return;

    this.connection.send(
      'consent',
      { surgeonId: answer.surgeonId, allow: answer.allow },
      this.connection.nextSequence(),
    );
    this.options.onNote?.(answer.allow ? 'You give them leave.' : 'You will not have it.');
  }

  private resolveTalkChoice(result: ReturnType<TalkMenu['chooseId']>): void {
    if (!result) {
      this.closeTalk();
      this.options.onNote?.('You think better of it.');
      return;
    }

    // Buying takes a second key — which thing off the stall — so the menu can
    // answer with a new prompt instead of a request. It is still open.
    if (result.kind === 'prompt') {
      const request = this.talk.request();
      if (request) this.options.onTalk?.(request);
      return;
    }

    const { choice } = result;
    const waiting = this.talk.request('Waiting for an answer…', true);
    if (waiting) this.options.onTalk?.(waiting);
    this.connection.send(
      'talk',
      {
        targetId: choice.targetId,
        topic: choice.topic,
        ...(choice.subject === undefined ? {} : { subject: choice.subject }),
      },
      this.connection.nextSequence(),
    );
  }

  /** Resolve a response button, or dismiss the conversation. */
  chooseTalk(id: string | null): void {
    if (id === null) {
      this.closeTalk();
      this.options.onNote?.('You think better of it.');
      return;
    }
    this.resolveTalkChoice(this.talk.chooseId(id));
  }

  private closeTalk(): void {
    this.talk.close();
    this.options.onTalk?.(null);
    this.input.setEnabled(true);
  }

  private resolveGesture(key: string): void {
    const gesture = this.gestures.choose(key);
    if (!gesture) {
      this.options.onNote?.('You keep your hands to yourself.');
      return;
    }

    this.connection.send(
      'gesture',
      { kind: gesture, direction: this.prediction.self?.facing ?? 'n' },
      this.connection.nextSequence(),
    );
  }

  private resolveAim(key: string): void {
    if (!this.aim.active) return;

    const part = this.aim.choose(key);
    if (!part) {
      this.options.onNote?.('You lower your aim.');
      return;
    }

    const target = this.adjacentTarget();
    if (!target) {
      this.options.onNote?.('There is nobody within reach.');
      return;
    }

    this.connection.send(
      'attack',
      { targetId: target, part: part.id },
      this.connection.nextSequence(),
    );
    this.options.onNote?.(`You aim for the ${part.name}.`);
  }

  /**
   * Open the verb menu against a helpless body beside you (M2-07).
   *
   * Refused out loud when there is nobody to work on, because the alternative —
   * a menu that opens on nothing — teaches the player that the key sometimes
   * does nothing rather than that there is nobody there.
   */
  private openInteract(): void {
    // Somebody standing beside you who lives here gets talked to; somebody
    // lying at your feet gets the verbs (M4-08). One key, and which menu it
    // opens is a question about who is there rather than about a mode.
    const neighbour = this.adjacentVillager();
    if (neighbour) {
      const greeting = this.talk.show(neighbour);
      if (greeting) {
        const request = this.talk.request();
        if (request) {
          this.input.setEnabled(false);
          this.options.onTalk?.(request);
        }
        return;
      }
    }

    const body = this.adjacentBody();
    if (!body) {
      this.options.onNote?.('There is nobody at your feet.');
      return;
    }
    const prompt = this.interact.show(body);
    if (prompt) this.options.onNote?.(prompt);
  }

  /**
   * Gather what is under your feet (M3-03).
   *
   * The client checks what it can see before sending, the way `openInteract`
   * does: a rejection that arrives 50 ms later reads as the key being broken,
   * where "there is nothing to gather here" reads as the ground being bare.
   * The server still decides — this is a courtesy, not a rule.
   */
  private forage(): void {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return;

    const patch = world.floraAt(self.x, self.y);
    if (!patch) {
      this.options.onNote?.('There is nothing to gather here.');
      return;
    }
    if (patch.standing === 'picked-over') {
      this.options.onNote?.(`The ${patch.name} here has been picked bare.`);
      return;
    }

    this.connection.send('forage', {}, this.connection.nextSequence());
  }

  /**
   * Strike a light (M3-05).
   *
   * Refused locally when there is nothing to burn, for the reason `forage` is:
   * a rejection 50 ms later reads as the key being broken, where "you have
   * nothing to burn" reads as an empty satchel. Whether the tinder *takes* is
   * the server's business and the weather's — the client is not told the odds
   * and could not compute them if it were.
   */
  private kindle(): void {
    const satchel = this.ownSelf?.satchel;
    if (satchel && satchel.items.length === 0) {
      this.options.onNote?.('You have nothing to burn.');
      return;
    }
    this.connection.send('kindle', {}, this.connection.nextSequence());
  }

  /**
   * Open the verb menu (M3-04).
   *
   * Everything in it comes off the last snapshot's `self.affordances`, which the
   * server built from the same fault functions its intent handlers call. The
   * client works nothing out for itself — that was the whole of M3-04, and the
   * hand-written "is there a carcass in reach, is there a fire, is there fuel"
   * this method used to do is exactly what it deleted.
   */
  private openVerbs(): void {
    this.openMenuAt(null);
  }

  /**
   * Open the one menu, about a square (RC2-07).
   *
   * `null` is the camp key's menu — what you can do where you stand. A tile is
   * the contextual version — what you can do with *that*, near to far: look at it,
   * mark it, and then, when it is the square under your own feet, everything
   * the server says you can do here.
   *
   * One menu rather than two, because two menus are two focus models, two key
   * conventions and two places for a verb to go missing. The list differs; the
   * component, the keys and the resolution rule do not.
   */
  private openMenuAt(tile: { readonly x: number; readonly y: number } | null): void {
    const self = this.ownSelf;
    const predicted = this.prediction.self;
    if (!self || !predicted) return;

    const own = tile === null || (tile.x === predicted.x && tile.y === predicted.y);
    const subject = tile === null ? null : this.subjectAt(tile);

    const items: MenuItem[] = [];

    // Looking is free and always answers (M0-06's rule), so it is always the
    // first row — and it is the row that teaches a mouse player that the
    // keyboard has an `x` for the same thing.
    if (tile) items.push({ id: MENU_LOOK, key: 'l', name: 'look' });
    if (subject?.actor && !own) items.push({ id: MENU_TARGET, key: 't', name: 'mark as target' });

    const context: VerbContext = {
      affordances: self.affordances,
      needs: self.needs,
      satchel: self.satchel,
      targetId: this.adjacentBody()?.id ?? null,
    };

    if (own) items.push(...actionMenuItems(context));

    if (items.length === 0) {
      this.options.onNote?.('There is nothing to do here.');
      return;
    }

    // Named by what is on the square when there is something — the design's
    // context menu is titled `yarrow`, not `Here` — and by the place otherwise.
    // `examineCard` decides what a square is called, so the menu heading and the
    // look card cannot call one thing two names.
    const named = subject ? examineCard(subject, '') : null;
    const place = actionMenuTitle(context);
    const title = named && named.kind !== 'ground' ? named.title : place;

    // Build the list once, against this snapshot, and hand it to the focused
    // island. The keyboard goes with it — suspended here the way the chat line
    // suspends it — so a movement key pressed at the menu moves nobody, and it
    // comes back when `chooseAction` closes the menu.
    // One overlay at a time: the card answers a question and the menu asks one,
    // and two backdrops over one map is two things to escape from.
    if (this.look.active) this.closeLook();

    this.verbContext = context;
    this.menuTile = tile;
    this.actionMenuOpen = true;
    this.input.setEnabled(false);
    this.publishHud();
    this.options.onActions?.({
      title,
      items,
      ...(own && title !== place ? { subtitle: place } : {}),
    });
  }

  /**
   * Resolve a choice from the action-menu island (RC1-05): a verb id, or `null`
   * for a cancel. Always closes the menu and hands the keyboard back to the
   * canvas first, so a refusal note lands after the overlay is gone.
   */
  chooseAction(id: string | null): void {
    const context = this.verbContext;
    const tile = this.menuTile;
    this.closeActionMenu();

    if (id === null) {
      this.options.onNote?.('You leave it be.');
      return;
    }

    // The two rows that are about the square rather than about the rules. They
    // resolve here rather than through `resolveVerbChoice` because neither is an
    // affordance: the server has no opinion about where you point your eyes.
    if (id === MENU_LOOK) {
      if (tile) this.openLookAt(tile);
      return;
    }
    if (id === MENU_TARGET) {
      if (tile) this.markTarget(tile);
      return;
    }

    if (context === null) return;
    this.performVerb(context, id as IntentType);
  }

  /** Resolve and send one verb through the same authoritative affordance path. */
  private performVerb(context: VerbContext, verb: IntentType): void {
    const result = resolveVerbChoice(context, verb);
    if (!result) return;

    // The reason the server gave, read back at the player. A menu that simply
    // closed on a refused verb would be M3-03's silent cancel again.
    if ('refused' in result) {
      this.options.onNote?.(capitaliseNote(result.refused));
      return;
    }

    const { choice } = result;
    const payload =
      choice.verb === 'eat'
        ? { itemId: choice.itemId }
        : choice.verb === 'rest'
          ? { on: choice.on }
          : choice.targetId
            ? { targetId: choice.targetId }
            : {};

    this.connection.send(choice.verb, payload, this.connection.nextSequence());
  }

  /** Fold the action menu shut and take the keyboard back. */
  private closeActionMenu(): void {
    if (!this.actionMenuOpen) return;
    this.actionMenuOpen = false;
    this.verbContext = null;
    this.menuTile = null;
    this.options.onActions?.(null);
    this.input.setEnabled(true);
    this.publishHud();
  }

  /**
   * Run a hotbar slot (RC2-07).
   *
   * The bar's ids *are* keymap action kinds, so a click goes through the exact
   * same `command` the key press does. There is no second dispatch table and no
   * mouse-only path a verb could go missing from — which is the same argument
   * M3-04 made for the pointer and keyboard context paths agreeing.
   */
  hotbarAction(id: string): void {
    this.command({ kind: id } as Action);
  }

  /**
   * Run a verb chosen on the look card (RC2-07).
   *
   * `target` is the card's own — it is about the square, not about the rules,
   * so it goes where the context menu's row goes rather than through the
   * keymap. Everything else is a binding, and takes the same door a key press
   * does.
   *
   * The card closes either way: the question has been answered and acted on,
   * and a card left open over the answer would be describing a patch the player
   * has just knelt down in.
   */
  examineVerb(id: string): void {
    const at = this.look.at;
    this.closeLook();
    if (id === 'target') {
      if (at) this.markTarget(at);
      return;
    }
    this.hotbarAction(id);
  }

  /** Point the active Look cursor at a clicked square and ask about it. */
  private lookAt(tile: { readonly x: number; readonly y: number }): void {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self || !this.look.active) {
      console.warn('[look-debug] tile click ignored before Look could inspect it', {
        tile,
        worldReady: Boolean(world),
        selfReady: Boolean(self),
        lookActive: this.look.active,
      });
      return;
    }

    const at = this.look.moveTo(tile, self, world, world.sky.sight);
    if (at) {
      console.info('[look-debug] tile accepted by Look', {
        tile: at,
        self: { x: self.x, y: self.y },
        sight: world.sky.sight,
      });
      this.examine(at);
    } else {
      console.warn('[look-debug] tile refused by client Look bounds', {
        tile,
        self: { x: self.x, y: self.y },
        sight: world.sky.sight,
        world: { width: world.width, height: world.height },
      });
      this.options.onNote?.('That is too far away to make out.');
    }
    this.publishHud();
  }

  /** Enter Look deliberately, then inspect the chosen context-menu square. */
  private openLookAt(tile: { readonly x: number; readonly y: number }): void {
    const self = this.prediction.self;
    if (!self) return;

    if (this.rangedAimActive) this.closeRangedAim();
    if (!this.look.active) this.look.open(self);
    this.lookAt(tile);
  }

  /**
   * Mark whatever is on a square (RC2-07).
   *
   * One row, two meanings, and the weapon in your hands picks which: a bow
   * raises and locks on, a blade squares up to somebody already within reach.
   * Both are the intentions the keyboard already has (`a` and `z`) — this is the
   * mouse's door to them, not a third rule.
   */
  private markTarget(tile: { readonly x: number; readonly y: number }): boolean {
    const world = this.world;
    if (!world) return false;

    const actor = world.actorAt(tile.x, tile.y);
    if (!actor) {
      this.options.onNote?.('There is nobody there.');
      return false;
    }

    const range = this.ownSelf?.combat?.weapon?.range;
    if (range !== null && range !== undefined) {
      this.rangedAimActive = true;
      this.rangedTarget = actor;
      const readout = this.rangedReadout();
      this.options.onNote?.(
        readout
          ? `Target locked: ${actor.name} · ${aimReadoutText(readout)}. Press A to loose.`
          : `Target locked: ${actor.name}. Press A to loose.`,
      );
      this.publishHud();
      return true;
    }

    if (this.adjacentTarget() === actor.id) {
      this.connection.send('engage', { targetId: actor.id }, this.connection.nextSequence());
      return true;
    }

    this.options.onNote?.(`${capitaliseNote(actor.name)} is out of reach.`);
    return false;
  }

  /**
   * Tell the hotbar what only `Game` knows: which overlays are open, whether the
   * cursor is out, and who is standing within reach.
   *
   * Pushed on every change rather than polled, so a slot lights the frame its
   * stance is taken — the same contract `onSelf` keeps for the strip.
   */
  private publishHud(): void {
    const self = this.prediction.self;
    this.options.onHud?.({
      // Bow targeting borrows the same cursor, but it is a different stance.
      // Calling that "Look" is what made the stuck targeting state appear to
      // be an active inspection mode in the playtest.
      looking: this.look.active && !this.rangedAimActive,
      targeting: this.look.active && this.rangedAimActive,
      satchelOpen: this.satchelOpen,
      sheetOpen: this.sheetOpen,
      battleOpen: this.battleOpen,
      adjacent: this.adjacentName(),
      engaged: this.engagedName(),
      rangedTarget: this.rangedTarget?.name ?? null,
      floraUnderfoot: self ? (this.world?.floraAt(self.x, self.y)?.name ?? null) : null,
    });
  }

  /** Whoever is standing next to the viewer, by name, for the engage slot. */
  private adjacentName(): string | null {
    const id = this.adjacentTarget();
    return id === null ? null : (this.world?.entity(id)?.name ?? null);
  }

  /** The opponent currently holding the viewer in an exchange, for the strike slot. */
  private engagedName(): string | null {
    const id = this.ownSelf?.combat?.engagedWith;
    return id === null || id === undefined ? null : (this.world?.entity(id)?.name ?? null);
  }

  private toggleSatchel(): void {
    this.setSatchelOpen(!this.satchelOpen);
  }

  /**
   * Close the satchel from the overlay itself (RC1-05) — the window's close
   * button routes here so `Game`'s own open flag stays in step with what the
   * React island is showing, and pressing `i` afterwards reopens it.
   */
  closeSatchel(): void {
    if (this.satchelOpen) this.setSatchelOpen(false);
  }

  /** Drop the exact row the server projected into the satchel popup. */
  dropItem(stackKey: string): void {
    this.connection.send('drop', { stackKey }, this.connection.nextSequence());
  }

  /** Equip or stow the weapon represented by one authoritative satchel row. */
  equipItem(stackKey: string): void {
    this.connection.send('equip', { stackKey }, this.connection.nextSequence());
  }

  /** Put the current physical weapon into the satchel. */
  stowWeapon(): void {
    this.connection.send('equip', { stow: true }, this.connection.nextSequence());
  }

  /** Run one making action surfaced by the character sheet. */
  craft(verb: 'build' | 'cook' | 'prepare'): void {
    const self = this.ownSelf;
    if (!self) return;
    this.performVerb(
      {
        affordances: self.affordances,
        needs: self.needs,
        satchel: self.satchel,
        targetId: null,
      },
      verb,
    );
  }

  /**
   * The satchel's DOM lives in the shell, like the battle view's and the chat
   * line's. `Game` owns whether it is open and nothing else — what is *in* it
   * arrives with every `onSelf`, so the shell redraws from the snapshot it is
   * already being handed rather than from a second channel that could disagree
   * with it.
   */
  private setSatchelOpen(open: boolean): void {
    this.satchelOpen = open;
    this.options.onSatchel?.(open);
    this.publishHud();
  }

  private toggleSheet(): void {
    this.setSheetOpen(!this.sheetOpen);
  }

  /**
   * Close the character sheet from the overlay itself (RC2-03) — the same round
   * trip the satchel makes, so `Game`'s open flag stays in step with the island
   * and pressing `p` afterwards reopens it.
   */
  closeSheet(): void {
    if (this.sheetOpen) this.setSheetOpen(false);
  }

  /**
   * The sheet's DOM lives in the shell like the satchel's, and holds the same
   * contract: `Game` owns whether it is open, and what is *in* it arrives with
   * every `onSelf`, so the island redraws from the snapshot the shell already has.
   */
  private setSheetOpen(open: boolean): void {
    this.sheetOpen = open;
    this.options.onSheet?.(open);
    this.publishHud();
  }

  private resolveInteract(key: string): void {
    const { choice, prompt } = this.interact.choose(key);

    // Staying open to ask again — the execute confirmation, and the only time
    // this menu takes a second keystroke.
    if (prompt) {
      this.options.onNote?.(prompt);
      return;
    }
    if (!choice) {
      this.options.onNote?.('You leave them be.');
      return;
    }

    // `drag` needs a direction the menu does not ask for; the honest default is
    // the way the dragger is already facing, which is the way they came.
    // `drag` used to need a direction; since M4-06 it is a grip rather than a
    // shove, and the body goes wherever its carrier walks.
    const payload =
      choice.verb === 'execute'
        ? { targetId: choice.targetId, confirm: true }
        : { targetId: choice.targetId };

    this.connection.send(choice.verb, payload, this.connection.nextSequence());
  }

  /**
   * The helpless body next to the viewer, if there is one.
   *
   * Separate from `adjacentTarget` below on purpose: that one answers "who can
   * I hit", which is a different question with a different answer. Standing
   * between a wolf and a downed friend, `e` must reach the friend and a bump
   * must reach the wolf.
   */
  private adjacentBody(): EntitySnapshot | null {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return null;

    for (const direction of Object.keys(DIRECTION_VECTORS) as Direction[]) {
      const [dx, dy] = DIRECTION_VECTORS[direction];
      const actor = world.actorAt(self.x + dx, self.y + dy);
      if (actor && isHelplessTarget(actor)) return actor;
    }
    return null;
  }

  /**
   * Whoever lives here and is standing beside the viewer (M4-08).
   *
   * Its own scan beside `adjacentBody` for the reason that one is beside
   * `adjacentTarget`: "who can I talk to" and "who can I work on" are different
   * questions with different answers, and standing between the Meddyg and a
   * fallen friend must reach the friend with the verbs and her with the words.
   */
  private adjacentVillager(): EntitySnapshot | null {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return null;

    for (const direction of Object.keys(DIRECTION_VECTORS) as Direction[]) {
      const [dx, dy] = DIRECTION_VECTORS[direction];
      const actor = world.actorAt(self.x + dx, self.y + dy);
      if (actor && isTalkable(actor)) return actor;
    }
    return null;
  }

  /** Whoever is standing next to the viewer. The aimed blow's target. */
  private adjacentTarget(): string | null {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return null;

    for (const direction of Object.keys(DIRECTION_VECTORS) as Direction[]) {
      const [dx, dy] = DIRECTION_VECTORS[direction];
      const actor = world.actorAt(self.x + dx, self.y + dy);
      if (actor) return actor.id;
    }
    return null;
  }

  /** Aim at a named part from the body diagram — the mouse path (M2-06). */
  aimAt(partId: string): void {
    const target = this.adjacentTarget();
    if (!target) {
      this.options.onNote?.('There is nobody within reach.');
      return;
    }
    this.connection.send(
      'attack',
      { targetId: target, part: partId },
      this.connection.nextSequence(),
    );
  }

  private toggleGuard(): void {
    const up = this.ownSelf?.combat?.guarding ?? false;
    this.connection.send('guard', { on: !up }, this.connection.nextSequence());
  }

  /** Explicitly enter a melee exchange; movement never does this implicitly. */
  private engage(): void {
    const target = this.adjacentTarget();
    if (!target) {
      this.options.onNote?.('There is nobody close enough to engage.');
      return;
    }
    this.connection.send('engage', { targetId: target }, this.connection.nextSequence());
  }

  /**
   * Drop into a crouch, or straighten up (M5-03).
   *
   * Toggled against the server's own answer rather than against a local flag,
   * exactly as the guard is: the stance is broken by things the client does not
   * decide — a swing, a blow landing — and a toggle that trusted its own memory
   * would tell a player they were still creeping after a wolf had knocked them
   * out of it.
   */
  private toggleSneak(): void {
    const quiet = this.ownSelf?.combat?.sneaking ?? false;
    this.connection.send('sneak', { on: !quiet }, this.connection.nextSequence());
  }

  private setBattleOpen(open: boolean): void {
    this.battleOpen = open;
    this.options.onBattle?.(open);
    this.publishHud();
  }

  /** Actors the log pane is hovering. The renderer lights them up. */
  setHighlight(actors: readonly string[]): void {
    this.highlighted = new Set(actors);
  }

  private toggleLook(): void {
    const self = this.prediction.self;
    if (!self) {
      console.warn('[look-debug] Look toggle ignored: player snapshot is not ready');
      return;
    }

    // Target selection and inspection share a cursor but never an intention.
    // Pressing X (or clicking Look) while choosing a bow target switches the
    // existing cursor to Look instead of leaving target selection in front of
    // every subsequent tile click.
    if (this.rangedAimActive) {
      console.info('[look-debug] switching ranged cursor to Look', { self });
      this.closeRangedAim();
      if (!this.look.active) this.look.open(self);
      this.examineSubject = null;
      this.options.onExamine?.(null);
      this.publishHud();
      return;
    }

    if (this.look.active) {
      console.info('[look-debug] Look toggled off', { cursor: this.look.at, self });
      this.closeLook();
      return;
    }

    // Look is a stance, not a one-shot examination. Turning it on only exposes
    // the cursor; a deliberate click asks the question and opens the card.
    this.look.open(self);
    console.info('[look-debug] Look toggled on', { cursor: this.look.at, self });
    this.publishHud();
  }

  private stepCursor(direction: Parameters<Look['step']>[0]): void {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return;

    this.look.step(direction, self, world, world.sky.sight);
  }

  /** Ask the server what is there. It composes the description, not us. */
  private examine(at: { x: number; y: number }): void {
    // Captured now rather than when the answer lands: the description is about
    // the square that was asked about, and the player may have moved by then.
    this.examineSubject = this.subjectAt(at);
    const seq = this.connection.nextSequence();
    console.info('[look-debug] client requesting examine', {
      seq,
      at,
      subject: this.examineSubject,
    });
    this.connection.send('examine', { x: at.x, y: at.y }, seq);
  }

  /** Turn Look off explicitly, taking both its cursor and any open card away. */
  private closeLook(): void {
    this.look.close();
    this.examineSubject = null;
    this.options.onExamine?.(null);
    this.publishHud();
  }

  /**
   * Dismiss only the description window. Look remains lit until the player
   * clicks Look (or presses its key) again; the cursor, mode and window are
   * deliberately three separate pieces of state.
   */
  closeExamine(): void {
    if (!this.examineSubject) return;
    this.examineSubject = null;
    this.options.onExamine?.(null);
  }

  /** Canvas pixels from a pointer event, at the canvas's backing resolution. */
  private canvasPixel(event: MouseEvent | PointerEvent): { x: number; y: number } {
    const bounds = this.canvas.getBoundingClientRect();
    const scaleX = bounds.width === 0 ? 1 : this.canvas.width / bounds.width;
    const scaleY = bounds.height === 0 ? 1 : this.canvas.height / bounds.height;
    return {
      x: (event.clientX - bounds.left) * scaleX,
      y: (event.clientY - bounds.top) * scaleY,
    };
  }

  /** Hover moves the cursor but asks nothing — only a click is a question. */
  private readonly onPointerMove = (event: PointerEvent): void => {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self || !this.look.active) return;

    this.look.moveTo(
      tileAtPixel(this.canvasPixel(event), this.camera, this.viewport),
      self,
      world,
      world.sky.sight,
    );
  };

  /**
   * See every Look click before a HUD element can stop it reaching the canvas.
   * This is deliberately observational: the trace will tell us whether the
   * correct repair belongs at the application boundary or later in the path.
   */
  private readonly onLookPointerTrace = (event: PointerEvent): void => {
    if (!this.look.active) return;

    const bounds = this.canvas.getBoundingClientRect();
    const insideCanvas =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;
    const describe = (target: EventTarget): string => {
      if (!(target instanceof Element)) return target.constructor.name;
      const id = target.id ? `#${target.id}` : '';
      const classes = [...target.classList].map((name) => `.${name}`).join('');
      return `${target.tagName.toLowerCase()}${id}${classes}`;
    };

    console.info('[look-debug] browser pointerdown', {
      button: event.button,
      client: { x: event.clientX, y: event.clientY },
      insideCanvas,
      target: event.target ? describe(event.target) : null,
      path: event
        .composedPath()
        .filter((target): target is EventTarget => target instanceof EventTarget)
        .slice(0, 8)
        .map(describe),
    });
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return;

    if (this.rangedAimActive && event.button === 0) {
      const tile = tileAtPixel(this.canvasPixel(event), this.camera, this.viewport);
      if (!this.markTarget(tile)) {
        this.options.onNote?.('Choose a visible actor to target.');
        return;
      }
      if (this.look.active) this.closeLook();
      return;
    }

    // The action gesture is handled by `contextmenu`, not pointerdown. Browsers
    // are allowed to fire the latter before they decide to show their own menu;
    // handling it here made a right-click briefly inspect a tile, while leaving
    // control-click vulnerable to the browser menu instead of the game's.
    if (event.button !== 0) return;

    const pixel = this.canvasPixel(event);
    const tile = tileAtPixel(pixel, this.camera, this.viewport);
    if (this.look.active) {
      console.info('[look-debug] canvas received pointerdown', {
        client: { x: event.clientX, y: event.clientY },
        pixel,
        tile,
        self: { x: self.x, y: self.y },
        sight: world.sky.sight,
      });
    }
    this.lookAt(tile);
  };

  /** Take the browser's context-menu gesture and inspect that tile. */
  private readonly onContextMenu = (event: MouseEvent): void => {
    if (event.target && event.target !== this.canvas) return;
    event.preventDefault();
    const world = this.world;
    const self = this.prediction.self;
    if (!world || !self) return;
    this.lookAt(tileAtPixel(this.canvasPixel(event), this.camera, this.viewport));
  };

  private submitInput(): void {
    const world = this.world;
    const action = this.input.current();

    // In look mode the direction keys steer the cursor instead of the
    // character. Walking and looking at once would fight over the same keys.
    if (this.look.active) return;

    if (!world || !action || action.kind !== 'move') return;

    // Occupancy is never an attack.  An adjacent actor simply blocks the step;
    // the player can explicitly engage (`z`) or keep walking another way.
    if (this.bump(world, action.direction)) return;

    const seq = this.connection.nextSequence();
    const move = this.prediction.request(world, action.direction, this.tick, seq);

    // `request` returns null when the client can already tell the server would
    // say no — a cooldown, or a wall. Not sending it saves a round trip and a
    // visible snap-back.
    if (!move) return;

    this.connection.send('move', { direction: move.direction }, seq);
  }

  /**
   * Whether an adjacent occupied tile blocks movement.  This is intentionally
   * not a bump attack: a collision cannot start a fight.
   */
  private bump(world: ClientWorld, direction: Direction): boolean {
    const self = this.prediction.self;
    if (!self) return false;

    const [dx, dy] = DIRECTION_VECTORS[direction];
    if (!world.actorAt(self.x + dx, self.y + dy)) return false;

    return true;
  }

  private render(now: number, deltaMs: number): void {
    const world = this.world;
    const renderer = this.renderer;
    if (!world || !renderer) return;

    const self = this.prediction.self;
    if (self) {
      this.camera.follow(self.x, self.y, deltaMs);
      // Walking away from the cursor must not strand it out of reach, where
      // every keystroke would be refused for a reason nothing on screen shows.
      this.look.reanchor(self, world.sky.sight);
    }

    const scene = buildScene(world, this.camera, this.viewport, now, this.highlighted);
    const sprites: Sprite[] = [...scene.sprites];
    if (self) {
      sprites.push(
        selfSprite(self, this.camera, this.viewport, this.ownSelf?.combat?.down ?? false),
      );
    }

    // Last, so it is on top of whatever it is pointing at.
    const cursor = this.look.at;
    if (cursor) sprites.push(cursorSprite(cursor, this.camera, this.viewport));

    renderer.draw(scene.terrain, sprites);
    if (this.rangedAimActive && self && this.rangedTarget) {
      const readout = this.rangedReadout();
      if (readout) {
        this.rangedOverlay?.show(readout, {
          from: self,
          target: this.rangedTarget,
          camera: this.camera,
          viewport: this.viewport,
        });
      }
    } else {
      this.rangedOverlay?.hide();
    }
  }

  /** The server tick the client believes it is on — for stamping local notes. */
  get currentTick(): number {
    return this.tick;
  }

  /** Grid dimensions in cells — for the DOM overlay to lay itself out against. */
  get grid(): { columns: number; rows: number } {
    return gridSize(this.viewport);
  }
}

/** A server reason reads as a sentence when it lands in the note line. */
function capitaliseNote(reason: string): string {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'Not now.';
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}.`;
}
